// ============================================================================
// client/creusage.ts — QUATRE FAÇONS DE CREUSER
// ----------------------------------------------------------------------------
// Demande de Claude-Alain (13/08) : « je veux 4 nouvelles animations et effets de creusage ».
//
// Jusqu'ici, creuser n'avait qu'UN geste (cycle `dig` : armé → descente → impact) et les gravats
// ne changeaient que de couleur. Le slot `pickaxe` du catalogue, lui, ne pilotait rien du tout en
// jeu : il choisissait le SON d'impact à la Mine, et c'est tout. Deux pièces dedans depuis le
// début, pour un slot invisible pendant la partie.
//
// ⚠️ LA RÈGLE APPRISE SUR LES FRAPPES DE MAILLET (06/08), et la seule qui compte ici. La première
// version des six frappes a été rejetée d'un mot — « on dirait 6× les mêmes » — parce qu'elles
// partageaient leurs poses, leur courbe de vitesse et leur effet ; seule la couleur changeait.
// Pour que N gestes paraissent différents, il leur faut TROIS ÉTAGES DISTINCTS :
//   1. leurs propres POSES (écrites pour eux, rien de partagé) ;
//   2. leur propre COURBE DE VITESSE (le temps d'arrêt du pilon valait tous les effets) ;
//   3. leur propre EFFET (ce que la brique crache, et comment).
// Les quatre ci-dessous respectent les trois. Si un jour ils se ressemblent, c'est qu'un étage a
// été mutualisé.
// ============================================================================

import { melange, type Pose } from './papier'

export type Creusage = 0 | 1 | 2 | 3
export const CREUSAGE_NOMS = [
  'Le tailleur',   // 0 — trois petits coups de burin, secs et rapides
  'Le bûcheron',   // 1 — un grand coup par-dessus l'épaule, lourd
  'La vibreuse',   // 2 — pas de coup : ça tremble, en continu
  'Le sourcier',   // 3 — un geste lent, la brique se défait toute seule
] as const

/** L'id du loot `pickaxe` qui porte chaque style. La pioche équipée décide du geste. */
export const CREUSAGE_LOOTS: readonly string[] = [
  'pioche-burin', 'pioche-hache', 'pioche-vibreuse', 'pioche-baguette',
]

/** Ce que la brique crache. Peint par client/particles.ts — un étage à part entière. */
export type EffetCreusage = 'eclats' | 'blocs' | 'poudre' | 'lueur'
export const CREUSAGE_EFFETS: readonly EffetCreusage[] = ['eclats', 'blocs', 'poudre', 'lueur']

/* ─────────────────────────── LES COURBES DE VITESSE ─────────────────────────
 * Chacune raconte un rapport au poids : le burin ne pèse rien et repart aussitôt,
 * la hache s'arme longtemps et tombe d'un coup, la vibreuse ne s'arrête jamais,
 * la baguette ne force sur rien. */
/** LE BURIN : trois coups dans le temps d'un seul. C'est la répétition qui fait le geste. */
const burin = (t: number): number => { const c = (t * 3) % 1; return c * c * (3 - 2 * c) }
/** LA HACHE : 55 % à armer (lentement, en montant), puis la chute s'accélère toute seule. */
const hache = (t: number): number => t < .55 ? (t / .55) * .3 : .3 + ((t - .55) / .45) ** 2 * .7
/** LA VIBREUSE : aucune progression, une oscillation. Le geste n'a ni début ni fin. */
const vibre = (t: number): number => .5 + Math.sin(t * Math.PI * 12) * .5
/** LA BAGUETTE : tout en douceur, elle s'installe et se pose. */
const doux = (t: number): number => t * t * (3 - 2 * t)

/* ──────────────────────────── LES CHORÉGRAPHIES ─────────────────────────────
 * Deux poses par style, écrites pour lui. Rien n'est partagé — c'est l'étage 1. */
const P = (o: Partial<Pose>): Pose => ({
  bassinY: 0, bassinRot: 0, buste: [0, 0, 0], tete: [0, 0, 0], outil: 'pioche',
  cuAV: .28, gnAV: .28, cuAR: -.3, gnAR: .3, epAV: 1.2, cdAV: -.5, epAR: .5, cdAR: -.42, foulard: .5, ...o,
} as Pose)

// 0 · LE TAILLEUR — les coudes près du corps, de petits coups au poignet. Le buste bouge à peine :
// c'est un travail de précision, pas de force.
const A0 = P({ nom: 'burin · armé', bassinY: 0, buste: [0, 0, -.04], tete: [1, 0, .06],
  epAV: 1.55, cdAV: -1.05, epAR: .35, cdAR: -.5, foulard: .3 })
const B0 = P({ nom: 'burin · touche', bassinY: 1, buste: [1, 1, .06], tete: [2, 1, -.08], impact: true,
  epAV: .55, cdAV: -.55, epAR: .4, cdAR: -.45, foulard: .55 })

// 1 · LE BÛCHERON — la pioche part TRÈS haut derrière la tête, le buste se cambre en arrière,
// puis tout le corps s'abat. Le bassin descend de neuf pixels à l'impact : on sent le poids.
const A1 = P({ nom: 'hache · armé haut', bassinY: -4, bassinRot: .16, buste: [-3, -4, -.3], tete: [-1, -4, .3],
  cuAV: .12, gnAV: .18, cuAR: -.2, gnAR: .2, epAV: 3.1, cdAV: -.2, epAR: .3, cdAR: -.25, foulard: .15 })
const B1 = P({ nom: 'hache · abattage', bassinY: 9, bassinRot: -.14, buste: [6, 9, .62], tete: [8, 7, -.34],
  impact: true, ecrase: .94,
  cuAV: .52, gnAV: .66, cuAR: -.44, gnAR: .62, epAV: -.95, cdAV: -.1, epAR: .7, cdAR: -.5, foulard: 1.3 })

// 2 · LA VIBREUSE — les deux bras tendus, tenus serrés, et TOUT LE CORPS qui tremble. Les deux
// poses sont volontairement très proches : c'est l'oscillation rapide qui fait lire la vibration,
// pas l'amplitude. (Une amplitude franche donnerait un coup, pas un tremblement.)
const A2 = P({ nom: 'vibreuse · tremblement bas', bassinY: 1, bassinRot: -.03, buste: [1, 1, .08], tete: [1, 2, -.04],
  cuAV: .34, gnAV: .4, cuAR: -.34, gnAR: .42, epAV: 1.5, cdAV: -.28, epAR: 1.3, cdAR: -.3, foulard: .7 })
const B2 = P({ nom: 'vibreuse · tremblement haut', bassinY: -1, bassinRot: .03, buste: [0, -1, .02], tete: [0, -1, .04],
  impact: true,
  cuAV: .3, gnAV: .34, cuAR: -.3, gnAR: .36, epAV: 1.62, cdAV: -.34, epAR: 1.42, cdAR: -.24, foulard: .5 })

// 3 · LE SOURCIER — pas d'outil qui frappe : la main s'ouvre vers la brique et le corps se
// redresse, presque immobile. Le seul geste de la maison où le héros ne force sur rien.
const A3 = P({ nom: 'sourcier · main tendue', bassinY: -1, bassinRot: .02, buste: [0, -1, -.06], tete: [1, -2, .1],
  cuAV: .2, gnAV: .22, cuAR: -.22, gnAR: .24, epAV: .2, cdAV: -.9, epAR: -.15, cdAR: -.35, foulard: .25 })
const B3 = P({ nom: 'sourcier · appel', bassinY: -3, bassinRot: -.02, buste: [1, -3, .04], tete: [2, -4, -.04],
  impact: true, etire: 1.04,
  cuAV: .16, gnAV: .18, cuAR: -.18, gnAR: .2, epAV: -.35, cdAV: -1.15, epAR: -.25, cdAR: -.3, foulard: .1 })

const CHOREGRAPHIES: readonly [Pose, Pose][] = [[A0, B0], [A1, B1], [A2, B2], [A3, B3]]

export interface TempsCreusage {
  pose: Pose
  /** progression APRÈS courbe de vitesse (0..1) — les effets s'en servent pour se déclencher */
  k: number
  /** vrai au moment précis où la brique encaisse : c'est là qu'on crache les gravats */
  frappe: boolean
}

/**
 * La pose et l'avancement d'un style à l'instant `t` (0..1 sur un cycle de creusage). PUR.
 * ⚠️ `frappe` ne vaut pas « k proche de 1 » : le tailleur touche TROIS fois par cycle et la
 * vibreuse touche en continu. Chaque style dit lui-même quand la brique encaisse.
 */
export function tempsCreusage(c: Creusage, t: number): TempsCreusage {
  const brut = Math.max(0, Math.min(1, t))
  const [a, b] = CHOREGRAPHIES[c] ?? CHOREGRAPHIES[0]!
  switch (c) {
    case 0: { // LE TAILLEUR : trois cycles courts dans le temps d'un seul geste
      const k = burin(brut)
      const phase = (brut * 3) % 1
      return { pose: melange(a, b, k), k, frappe: phase > .82 }
    }
    case 1: { // LE BÛCHERON : un seul impact, tout à la fin, après une longue montée
      const k = hache(brut)
      return { pose: melange(a, b, k), k, frappe: brut > .93 }
    }
    case 2: { // LA VIBREUSE : elle mord sans arrêt — un crachat toutes les deux oscillations
      const k = vibre(brut)
      return { pose: melange(a, b, k), k, frappe: (brut * 6) % 1 > .78 }
    }
    default: { // LE SOURCIER : la brique cède en silence, à la toute fin du geste
      const k = doux(brut)
      return { pose: melange(a, b, k), k, frappe: brut > .88 }
    }
  }
}

/** Le style porté par la pioche équipée. Sans pioche connue : le geste historique (le tailleur). */
export function creusageDuLoot(lootId: string | null | undefined): Creusage | null {
  if (!lootId) return null
  const i = CREUSAGE_LOOTS.indexOf(lootId)
  return i < 0 ? null : (i as Creusage)
}

/** L'effet de gravats d'un style. */
export function effetDuCreusage(c: Creusage): EffetCreusage {
  return CREUSAGE_EFFETS[c] ?? 'eclats'
}
